import { Label } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { Slider } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { X, Plus, Sparkles } from 'lucide-react'
import { useState } from 'react'
import type { EventInput } from '@creanizer/types'

interface EventFormVariationProps {
  input: EventInput
  onChange: (value: EventInput) => void
}

export function EventFormVariation({ input, onChange }: EventFormVariationProps) {
  const [uniqueElementInput, setUniqueElementInput] = useState('')
  const [avoidPatternInput, setAvoidPatternInput] = useState('')
  const [seedInput, setSeedInput] = useState('')

  const handleAddUniqueElement = () => {
    if (uniqueElementInput.trim()) {
      onChange({
        ...input,
        uniqueElements: [...(input.uniqueElements || []), uniqueElementInput.trim()]
      })
      setUniqueElementInput('')
    }
  }

  const handleRemoveUniqueElement = (index: number) => {
    onChange({
      ...input,
      uniqueElements: input.uniqueElements?.filter((_, i) => i !== index) || []
    })
  }

  const handleAddAvoidPattern = () => {
    if (avoidPatternInput.trim()) {
      onChange({
        ...input,
        avoidPatterns: [...(input.avoidPatterns || []), avoidPatternInput.trim()]
      })
      setAvoidPatternInput('')
    }
  }

  const handleRemoveAvoidPattern = (index: number) => {
    onChange({
      ...input,
      avoidPatterns: input.avoidPatterns?.filter((_, i) => i !== index) || []
    })
  }

  const handleAddSeed = () => {
    if (seedInput.trim()) {
      onChange({
        ...input,
        variationSeeds: [...(input.variationSeeds || []), seedInput.trim()]
      })
      setSeedInput('')
    }
  }

  const handleRemoveSeed = (index: number) => {
    onChange({
      ...input,
      variationSeeds: input.variationSeeds?.filter((_, i) => i !== index) || []
    })
  }

  return (
    <div className="space-y-4 border rounded-lg p-4 bg-secondary/20">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-5 h-5 text-primary" />
        <h3 className="font-semibold">バリエーション設定（最重要）</h3>
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="require-variation"
          checked={input.requireVariation || false}
          onCheckedChange={(checked) => 
            onChange({ ...input, requireVariation: checked as boolean })
          }
        />
        <Label htmlFor="require-variation" className="cursor-pointer">
          高バリエーションモード（推奨）
        </Label>
      </div>
      
      {input.requireVariation && (
        <div className="space-y-4 pl-6">
          <div>
            <Label>バリエーションレベル</Label>
            <Slider
              value={[input.variationLevel || 8]}
              onValueChange={([value]) => 
                onChange({ ...input, variationLevel: value })
              }
              min={1}
              max={10}
              step={1}
              className="mt-2"
            />
            <div className="flex justify-between text-xs mt-1">
              <span className="text-muted-foreground">低</span>
              <span className="font-bold text-primary">
                {input.variationLevel || 8}/10
              </span>
              <span className="text-muted-foreground">高</span>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              {input.variationLevel && input.variationLevel >= 8 
                ? '⚡ 非常に多様で予測不可能なバリエーション'
                : input.variationLevel && input.variationLevel >= 5
                ? '⚡ 適度なバリエーション'
                : '⚡ シンプルなバリエーション'}
            </p>
          </div>
          
          <div>
            <Label>独自要素（各バリエーションに含まれる固有の要素）</Label>
            <div className="flex gap-2 mt-2">
              <input
                type="text"
                value={uniqueElementInput}
                onChange={(e) => setUniqueElementInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddUniqueElement()
                  }
                }}
                placeholder="例: 時間制限、隠された罠、意外な協力者..."
                className="flex-1 px-3 py-2 border rounded-md text-sm"
              />
              <button
                onClick={handleAddUniqueElement}
                className="px-3 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {(input.uniqueElements || []).map((element, index) => (
                <Badge key={index} variant="secondary" className="flex items-center gap-1">
                  {element}
                  <button
                    onClick={() => handleRemoveUniqueElement(index)}
                    className="ml-1 hover:text-destructive"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
              {(!input.uniqueElements || input.uniqueElements.length === 0) && (
                <span className="text-xs text-muted-foreground">自動生成されます</span>
              )}
            </div>
          </div>
          
          <div>
            <Label>回避パターン（生成時に避けたい要素）</Label>
            <div className="flex gap-2 mt-2">
              <input
                type="text"
                value={avoidPatternInput}
                onChange={(e) => setAvoidPatternInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddAvoidPattern()
                  }
                }}
                placeholder="例: ご都合主義、突然の死亡、無意味な戦闘..."
                className="flex-1 px-3 py-2 border rounded-md text-sm"
              />
              <button
                onClick={handleAddAvoidPattern}
                className="px-3 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {(input.avoidPatterns || []).map((pattern, index) => (
                <Badge key={index} variant="destructive" className="flex items-center gap-1">
                  {pattern}
                  <button
                    onClick={() => handleRemoveAvoidPattern(index)}
                    className="ml-1 hover:text-destructive-foreground"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
              {(!input.avoidPatterns || input.avoidPatterns.length === 0) && (
                <span className="text-xs text-muted-foreground">デフォルトパターンが適用されます</span>
              )}
            </div>
          </div>

          <div>
            <Label>バリエーションシード（バリエーションの起点となるアイデア）</Label>
            <div className="flex gap-2 mt-2">
              <input
                type="text"
                value={seedInput}
                onChange={(e) => setSeedInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddSeed()
                  }
                }}
                placeholder="例: 深夜の森で光る目と遭遇..."
                className="flex-1 px-3 py-2 border rounded-md text-sm"
              />
              <button
                onClick={handleAddSeed}
                className="px-3 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {(input.variationSeeds || []).map((seed, index) => (
                <Badge key={index} variant="outline" className="flex items-center gap-1">
                  {seed}
                  <button
                    onClick={() => handleRemoveSeed(index)}
                    className="ml-1 hover:text-destructive"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
              {(!input.variationSeeds || input.variationSeeds.length === 0) && (
                <span className="text-xs text-muted-foreground">時刻ベースで自動生成されます</span>
              )}
            </div>
          </div>

          <div className="bg-primary/10 rounded-lg p-3">
            <p className="text-sm">
              <strong>💡 バリエーション生成について</strong>
            </p>
            <ul className="text-sm text-muted-foreground mt-2 space-y-1 list-disc list-inside">
              <li>各バリエーションは基本版から{(input.variationLevel || 8) * 5}%以上異なる内容になります</li>
              <li>最低5つの異なるバリエーションが生成されます</li>
              <li>各バリエーションには最低3つの固有要素が含まれます</li>
              <li>プレイヤーが予測できない意外な展開が含まれます</li>
            </ul>
          </div>
        </div>
      )}

      {!input.requireVariation && (
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="text-sm text-muted-foreground">
            バリエーションモードを有効にすると、同じイベントでも毎回異なる展開が楽しめます。
            TRPGのリプレイ性を高めるため、有効化を強く推奨します。
          </p>
        </div>
      )}
    </div>
  )
}